import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { cn } from '@/lib/utils';

type Gender = 'female' | 'male' | 'other';

interface EditGenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentGender: Gender | null;
  onSave: (gender: Gender) => void;
}

export function EditGenderModal({
  isOpen,
  onClose,
  currentGender,
  onSave,
}: EditGenderModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useAuth();
  const [gender, setGender] = useState<Gender | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const genderOptions: { value: Gender; label: string; description: string }[] = [
    { value: 'female', label: t('profile:modals.editGender.female'), description: t('profile:modals.editGender.femaleDesc') },
    { value: 'male', label: t('profile:modals.editGender.male'), description: t('profile:modals.editGender.maleDesc') },
    { value: 'other', label: t('profile:modals.editGender.other'), description: t('profile:modals.editGender.otherDesc') },
  ];

  // Initialize value when modal opens
  useEffect(() => {
    if (isOpen) {
      setGender(currentGender);
      setError(null);
    }
  }, [isOpen, currentGender]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!user || !gender) return;

    setIsSaving(true);
    setError(null);

    try {
      const { error: dbError } = await supabase
        .from('profiles')
        .update({ gender })
        .eq('id', user.id);

      if (dbError) throw dbError;

      onSave(gender);
      onClose();
    } catch (err) {
      console.error('Error saving gender:', err);
      setError(t('profile:modals.editGender.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.editGender.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('profile:modals.editGender.description')}
            </p>

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[10px] text-sm text-red-300">
                {error}
              </div>
            )}

            {/* Gender Options */}
            <div className="space-y-3">
              {genderOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setGender(option.value)}
                  className={cn(
                    'w-full p-4 rounded-[15px] border text-left transition-all',
                    gender === option.value
                      ? 'bg-pp-orange/20 border-pp-orange'
                      : 'bg-white/10 border-white/20 hover:bg-white/15'
                  )}
                >
                  <p className="font-medium text-white">{option.label}</p>
                  <p className="text-sm text-white/60">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !gender}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('common:actions.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
